import { Icon, IIconProps, type iconName } from '@/common/components/Icon'
import { getMenuIcon } from '@/common/constants/menu-icons'
import { cn } from '@/lib/utils'
import { Button } from './ui'
import { ArrowLeft } from 'lucide-react'
import { useNavigate, useParams } from 'react-router'

export function PageTitle({
  title,
  subtitle,
  icon,
  className,
  backButton,
  backButtonFunction,
  iconProps,
  hidePageIcon = false,
}: {
  title: string | React.ReactNode
  subtitle?: string | React.ReactNode
  icon?: iconName
  className?: string
  backButton?: boolean
  backButtonFunction?: () => void
  iconProps?: Omit<IIconProps, 'icon' | 'width' | 'height' | 'viewBox'>
  hidePageIcon?: boolean
}) {
  const titleString = typeof title === 'string' ? title : null
  const resolvedIcon: iconName =
    icon ?? (titleString !== null ? getMenuIcon(titleString) : 'home-2')
  const navigate = useNavigate()
  const { festivalName, editionId } = useParams()
  return (
    <div className={cn('flex items-center gap-4', className)}>
      {!hidePageIcon && (
        <div
          className={cn(
            'h-[44px] w-[44px] rounded-full relative',
            'bg-monochrome-5 backdrop-blur-md',
            'border-2 border-monochrome-120',
            'flex items-center justify-center',
            'shadow-[0_12px_40px_rgba(0,0,0,0.45)]',
          )}
        >
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1/2 h-1/2 bg-accent-1/40 rounded-3xl blur-sm" />
          <div
            className={cn(
              'h-[40px] w-[40px] rounded-full',
              'shadow-[0_0_24px_rgba(136,100%,51%,0.35)]',
              'flex items-center justify-center',
              'text-accent-1',
            )}
          >
            <Icon
              icon={resolvedIcon}
              width="18"
              height="18"
              viewBox="0 0 18 18"
              fill="text-accent-1"
              {...iconProps}
            />
          </div>
        </div>
      )}
      {backButton && (
        <Button
          variant="primary-outline"
          className="w-[44px] h-[44px] p-0 shrink-0 bg-transparent border-2 border-monochrome-120 hover:bg-monochrome-5"
          onClick={() =>
            backButtonFunction
              ? backButtonFunction()
              : navigate(`/stages/${festivalName}/${editionId}`)
          }
        >
          <ArrowLeft className="w-4 h-4 text-monochrome-1 shrink-0" />
        </Button>
      )}
      <div className="flex flex-col">
        {subtitle ? (
          <div className="text-xs font-medium text-monochrome-2">
            {subtitle}
          </div>
        ) : null}
        <div className="text-3xl font-semibold tracking-tight text-monochrome-1">
          {title}
        </div>
      </div>
    </div>
  )
}
